import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Component, Suspense, useEffect } from "react";
import { SWRConfig } from "swr";
import { swrFetcher } from "@/lib/swrFetcher";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { WorkspaceProvider, useWorkspace } from "@/lib/WorkspaceContext";
import { EntitlementsProvider } from "@/context/EntitlementsContext";
import { SessionProvider, useSession } from "@/context/SessionContext";
import RouteGuard from "@/components/RouteGuard";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { lazyWithPreloadAndRetry, lazyWithRetry } from "@/lib/lazyWithRetry";
import NotFoundPage from "@/components/NotFoundPage";
import { getRouteTier, ACCESS_TIERS } from "@/config/routeAccess";
import "@/App.css";
import "@/index.css";
import InstallPrompt from "@/components/InstallPrompt";
import LoginPage from "@/pages/LoginPage";
import AuthCallbackPage from "@/pages/AuthCallback";
import ComingSoonPage from "@/components/ComingSoonPage";
import { isFeatureEnabled } from "@/config/features";
import { legacyChartUrlToResearch } from "@/lib/researchSearch/researchSearchUrl";
import { SESSION_FETCH_OPTIONS } from "@/lib/session";

// Local dev only: prefix /api/ with REACT_APP_BACKEND_URL.
// Production MUST use same-origin /api (Cloudflare Pages Function). Do NOT set
// REACT_APP_BACKEND_URL on Cloudflare — it breaks HttpOnly cookies with BEHIND_PROXY.
const _BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const _USE_DEV_BACKEND_PREFIX = Boolean(_BACKEND) && process.env.NODE_ENV === "development";
const _originalFetch = window.fetch;
window.fetch = function (url, options = {}) {
  if (typeof url === "string" && url.startsWith("/api/") && _USE_DEV_BACKEND_PREFIX) {
    url = _BACKEND + url;
  }
  if (typeof url === "string" && url.includes("/api/")) {
    options = { ...SESSION_FETCH_OPTIONS, ...options, credentials: "include" };
  }
  return _originalFetch.call(this, url, options);
};

const Dashboard = lazyWithPreloadAndRetry(() => import("@/pages/Dashboard"));
const Holdings = lazyWithPreloadAndRetry(() => import("@/pages/Holdings"));
const ImportPage = lazyWithRetry(() => import("@/pages/ImportPage"));
const AuditLog = lazyWithRetry(() => import("@/pages/AuditLog"));
const CompanySearch = lazyWithRetry(() => import("@/pages/CompanySearch"));
const ResearchDesk = lazyWithRetry(() => import("@/pages/ResearchDesk"));
const ResearchBusinessProfile = lazyWithRetry(() => import("@/pages/ResearchBusinessProfile"));
const ResearchMetricView = lazyWithRetry(() => import("@/pages/ResearchMetricView"));
const ResearchMetricGuide = lazyWithRetry(() => import("@/pages/ResearchMetricGuide"));
const ResearchWorkspace = lazyWithRetry(() => import("@/pages/ResearchWorkspace"));
const ManualEntry = lazyWithRetry(() => import("@/pages/ManualEntry"));
const CapAnalysis = lazyWithRetry(() => import("@/pages/CapAnalysis"));
const QueueAllocator = lazyWithRetry(() => import("@/pages/QueueAllocator"));
const Analytics = lazyWithRetry(() => import("@/pages/Analytics"));
const TickerEvaluation = lazyWithRetry(() => import("@/pages/TickerEvaluation"));
const DividendTracker = lazyWithRetry(() => import("@/pages/DividendTracker"));
const TaxLossHarvesting = lazyWithRetry(() => import("@/pages/TaxLossHarvesting"));
const RebalancingCalculator = lazyWithRetry(() => import("@/pages/RebalancingCalculator"));
const CorrelationMatrix = lazyWithRetry(() => import("@/pages/CorrelationMatrix"));
const BenchmarkComparison = lazyWithRetry(() => import("@/pages/BenchmarkComparison"));
const MonteCarloSimulation = lazyWithRetry(() => import("@/pages/MonteCarloSimulation"));
const EmailReports = lazyWithRetry(() => import("@/pages/EmailReports"));
const BatchOperations = lazyWithRetry(() => import("@/pages/BatchOperations"));
const Watchlist = lazyWithRetry(() => import("@/pages/Watchlist"));
const DCAScheduler = lazyWithRetry(() => import("@/pages/DCAScheduler"));
const Journal = lazyWithRetry(() => import("@/pages/Journal"));
const Compare = lazyWithRetry(() => import("@/pages/Compare"));
const Screener = lazyWithRetry(() => import("@/pages/Screener"));
const NewsFeed = lazyWithRetry(() => import("@/pages/NewsFeed"));
const EarningsCalendar = lazyWithRetry(() => import("@/pages/EarningsCalendar"));
const PositionSizer = lazyWithRetry(() => import("@/pages/PositionSizer"));
const DividendProjector = lazyWithRetry(() => import("@/pages/DividendProjector"));
const WhatIf = lazyWithRetry(() => import("@/pages/WhatIf"));
const Backtest = lazyWithRetry(() => import("@/pages/Backtest"));
const FXExposure = lazyWithRetry(() => import("@/pages/FXExposure"));
const PortfolioTreemap = lazyWithRetry(() => import("@/pages/PortfolioTreemap"));
const HoldingsComparison = lazyWithRetry(() => import("@/pages/HoldingsComparison"));
const AuthTest = lazyWithRetry(() => import("@/pages/AuthTest"));
const BusinessAnatomyHarness = lazyWithRetry(() => import("@/e2e/BusinessAnatomyHarness"));
const Timeline = lazyWithRetry(() => import("@/pages/Timeline"));
const ShareCards = lazyWithRetry(() => import("@/pages/ShareCards"));
const SnapshotComparison = lazyWithRetry(() => import("@/pages/SnapshotComparison"));
const HoldingDetail = lazyWithRetry(() => import("@/pages/HoldingDetail"));
const HoldingCockpit = lazyWithRetry(() => import("@/pages/HoldingCockpit"));
const ThesisPage = lazyWithRetry(() => import("@/pages/ThesisPage"));
const NetWorth = lazyWithRetry(() => import("@/pages/NetWorth"));
const IncomeDashboard = lazyWithRetry(() => import("@/pages/IncomeDashboard"));
const ActionCenter = lazyWithRetry(() => import("@/pages/ActionCenter"));
const ResearchHub = lazyWithRetry(() => import("@/pages/ResearchHub"));
const MutualFunds = lazyWithRetry(() => import("@/pages/MutualFunds"));
const SectorHeatmap = lazyWithRetry(() => import("@/pages/SectorHeatmap"));
const PeerComparison = lazyWithRetry(() => import("@/pages/PeerComparison"));
const MarketOverview = lazyWithRetry(() => import("@/pages/MarketOverview"));
const MacroEnvironment = lazyWithRetry(() => import("@/pages/MacroEnvironment"));
const ResearchCopilot = lazyWithRetry(() => import("@/pages/ResearchCopilot"));
const PortfolioDoctor = lazyWithRetry(() => import("@/pages/PortfolioDoctor"));
const StrategyLabs = lazyWithRetry(() => import("@/pages/StrategyLabs"));
const BDDOverview = lazyWithRetry(() => import("@/pages/BDDOverview"));
const SystemLogicDictionary = lazyWithRetry(() => import("@/pages/SystemLogicDictionary"));
const InvestorDictionary = lazyWithRetry(() => import("@/pages/InvestorDictionary"));
const LearnToday = lazyWithRetry(() => import("@/pages/LearnToday"));
const DictionaryReviewQueue = lazyWithRetry(() => import("@/pages/DictionaryReviewQueue"));
const IntelligenceHub = lazyWithRetry(() => import("@/pages/IntelligenceHub"));
const PortfolioExplorer = lazyWithRetry(() => import("@/pages/PortfolioExplorer"));
const HealthDashboard = lazyWithRetry(() => import("@/pages/HealthDashboard"));
const FinancialWellnessPage = lazyWithRetry(() => import("@/pages/FinancialWellnessPage"));
const RiskPage = lazyWithRetry(() => import("@/pages/RiskPage"));
const ReviewPage = lazyWithRetry(() => import("@/pages/ReviewPage"));
const AppVersions = lazyWithRetry(() => import("@/pages/AppVersions"));
const DiscoverPage = lazyWithRetry(() => import("@/pages/DiscoverPage"));
const MoonshotDetail = lazyWithRetry(() => import("@/pages/MoonshotDetail"));
const LensDistinctiveness = lazyWithRetry(() => import("@/pages/LensDistinctiveness"));
const CandidatePipeline = lazyWithRetry(() => import("@/pages/CandidatePipeline"));
const LensAlerts = lazyWithRetry(() => import("@/pages/LensAlerts"));
const AllocationController = lazyWithRetry(() => import("@/pages/AllocationController"));
const ValuationDrilldown = lazyWithRetry(() => import("@/pages/ValuationDrilldown"));
const TransactionLedger = lazyWithRetry(() => import("@/pages/TransactionLedger"));
const PLAnalytics = lazyWithRetry(() => import("@/pages/PLAnalytics"));
const AIEngineDashboard = lazyWithRetry(() => import("@/pages/AIEngineDashboard"));
const AIClonePortfolio = lazyWithRetry(() => import("@/pages/AIClonePortfolio"));
const AICompare = lazyWithRetry(() => import("@/pages/AICompare"));
const AIApproval = lazyWithRetry(() => import("@/pages/AIApproval"));
const AIHistory = lazyWithRetry(() => import("@/pages/AIHistory"));
const AIWhatChanged = lazyWithRetry(() => import("@/pages/AIWhatChanged"));
const HoldingsIntradayReport = lazyWithRetry(() => import("@/pages/HoldingsIntradayReport"));
const AccountActivity = lazyWithRetry(() => import("@/pages/AccountActivity"));
const AccessControl = lazyWithRetry(() => import("@/pages/AccessControl"));
const PerformancePage = lazyWithRetry(() => import("@/pages/PerformancePage"));
const PortfolioQualityPage = lazyWithRetry(() => import("@/pages/PortfolioQualityPage"));
const SleeveOS = lazyWithRetry(() => import("@/pages/SleeveOS"));
const ReviewCadencePage = lazyWithRetry(() => import("@/pages/ReviewCadencePage"));
const InvestorJourneyPage = lazyWithRetry(() => import("@/pages/InvestorJourneyPage"));
const InvestorScorePage = lazyWithRetry(() => import("@/pages/InvestorScorePage"));
const ControllerSettings = lazyWithRetry(() => import("@/pages/ControllerSettings"));
const AICouncilBrief = lazyWithRetry(() => import("@/pages/AICouncilBrief"));
const RelationshipGraph = lazyWithRetry(() => import("@/pages/RelationshipGraph"));
const FirstPortfolioSetup = lazyWithRetry(() => import("@/pages/FirstPortfolioSetup"));
const Onboarding = lazyWithPreloadAndRetry(() => import("@/pages/Onboarding"));
const FundSettings = lazyWithRetry(() => import("@/pages/FundSettings"));
const DecisionLedger = lazyWithRetry(() => import("@/pages/DecisionLedger"));
const CashflowPlanner = lazyWithPreloadAndRetry(() => import("@/pages/CashflowPlanner"));
const PaydayHistory = lazyWithRetry(() => import("@/pages/PaydayHistory"));
const DebtOptimizer = lazyWithPreloadAndRetry(() => import("@/pages/DebtOptimizer"));
const FinancialSnapshot = lazyWithPreloadAndRetry(() => import("@/pages/FinancialSnapshot"));
const TaxAwareness = lazyWithPreloadAndRetry(() => import("@/pages/TaxAwareness"));
const PrinciplesLibrary = lazyWithPreloadAndRetry(() => import("@/pages/PrinciplesLibrary"));
const QuizCenter = lazyWithRetry(() => import("@/pages/QuizCenter"));
const BeamAcademy = lazyWithRetry(() => import("@/pages/BeamAcademy"));
const WealthStudio = lazyWithRetry(() => import("@/pages/WealthStudio"));
const BrainVisualizationPage = lazyWithRetry(() => import("@/pages/BrainVisualizationPage"));
const InvestmentSimulator = lazyWithRetry(() => import("@/pages/InvestmentSimulator"));

// Legacy Emergent AuthCallback removed — Supabase auth handled by pages/AuthCallback.js
const ONBOARDING_ALLOWED_PATHS = new Set(["/setup", "/onboarding", "/import", "/manual-entry"]);

// Auth-aware fetch is handled by the global fetch interceptor above

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header skeleton */}
      <div className="sticky top-0 z-50 w-full h-14 border-b border-border bg-background/95 backdrop-blur" />

      {/* Content skeleton */}
      <div className="md:ml-56 max-w-7xl mx-auto px-4 py-6 pb-28 md:pb-6 space-y-4">
        <div className="h-7 w-36 rounded-lg bg-muted animate-pulse" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
        <div className="h-44 rounded-xl bg-muted animate-pulse" />
        <div className="space-y-2">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      </div>

      {/* Mobile bottom nav skeleton */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-lg border-t border-border md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="grid grid-cols-5 gap-0.5 px-1 pt-1 pb-2">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="flex flex-col items-center gap-1 py-2">
              <div className="h-5 w-5 rounded bg-muted animate-pulse" />
              <div className="h-2 w-8 rounded bg-muted animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("[app-error-boundary]", error);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-background px-4 py-10 text-foreground">
        <div className="mx-auto max-w-sm rounded-lg border border-border bg-card p-4 shadow-sm">
          <h1 className="text-lg font-semibold">App needs a refresh</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Something failed while loading this screen. Refresh, or sign in again if your session expired.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
            <button
              type="button"
              className="rounded-md border border-border px-3 py-2 text-sm font-medium"
              onClick={() => { window.location.href = "/login"; }}
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function ProtectedRoute({ children, guard = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setupState, setupLoading } = useWorkspace();
  const { status: authState } = useSession();

  useEffect(() => {
    if (authState !== 'authenticated' || setupLoading || !setupState) return;
    if (setupState.should_show_onboarding && !ONBOARDING_ALLOWED_PATHS.has(location.pathname)) {
      navigate('/setup', { replace: true });
      return;
    }
    if (!setupState.should_show_onboarding && location.pathname === '/setup') {
      navigate('/dashboard', { replace: true });
    }
  }, [authState, setupLoading, setupState, location.pathname, navigate]);

  if (authState === 'checking') return <RouteLoadingFallback />;
  if (authState !== 'authenticated') return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  if (!guard) return children;
  return <RouteGuard tier={getRouteTier(location.pathname)}>{children}</RouteGuard>;
}

function PrivateOwnerRoute({ loadPrivate }) {
  return (
    <ProtectedRoute guard={false}>
      <RouteGuard tier={ACCESS_TIERS.OWNER} loadPrivate={loadPrivate} />
    </ProtectedRoute>
  );
}

function FeatureRoute({ feature, featureName, children }) {
  return (
    <ProtectedRoute>
      {isFeatureEnabled(feature) ? (
        children
      ) : (
        <ComingSoonPage featureName={featureName} />
      )}
    </ProtectedRoute>
  );
}

function LegacyChartRedirect() {
  const location = useLocation();
  return <Navigate to={`${legacyChartUrlToResearch(location.pathname)}${location.search}`} replace />;
}

function LegacyChartTickerRedirect() {
  const { ticker } = useParams();
  return <Navigate to={`/research/${String(ticker || "").toUpperCase()}`} replace />;
}

function AppRouter() {
  const location = useLocation();

  // Detect Supabase PKCE code or implicit flow tokens in URL
  const hasCode = new URLSearchParams(location.search).has('code');
  const hasHashTokens = location.hash?.includes('access_token=');

  useEffect(() => {
    let cancelled = false;
    const isPublicPath = ["/", "/login", "/auth/callback", "/auth-test"].includes(location.pathname);
    const isMobile = window.matchMedia?.("(max-width: 767px)")?.matches;
    if (isPublicPath || isMobile) return () => { cancelled = true; };

    const timer = window.setTimeout(() => {
      if (cancelled) return;
      const preloadCoreRoutes = () => {
        [
          Dashboard,
          Holdings,
        ].forEach(page => page.preload?.().catch?.(() => {}));
      };
      if (window.requestIdleCallback) {
        window.requestIdleCallback(preloadCoreRoutes, { timeout: 3000 });
      } else {
        window.setTimeout(preloadCoreRoutes, 1200);
      }
    }, 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [location.pathname]);

  // If auth tokens/code are present on any page, route to AuthCallback
  if (hasCode || hasHashTokens) {
    return <AuthCallbackPage />;
  }

  return (
    <RouteErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth-test" element={<AuthTest />} />
      <Route
        path="/e2e/business-anatomy/:ticker"
        element={
          process.env.REACT_APP_E2E_HARNESS === "1" ? (
            <Suspense fallback={<RouteLoadingFallback />}>
              <BusinessAnatomyHarness />
            </Suspense>
          ) : (
            <NotFoundPage />
          )
        }
      />
      <Route
        path="/setup"
        element={
          <ProtectedRoute>
            <FirstPortfolioSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timeline"
        element={
          <ProtectedRoute>
            <Timeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-finances"
        element={
          <ProtectedRoute>
            <FinancialSnapshot />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cashflow"
        element={
          <ProtectedRoute>
            <CashflowPlanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cashflow/payday-history"
        element={
          <ProtectedRoute>
            <PaydayHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/debt-optimizer"
        element={
          <ProtectedRoute>
            <DebtOptimizer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tax-awareness"
        element={
          <ProtectedRoute>
            <TaxAwareness />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holdings/:ticker"
        element={
          <ProtectedRoute>
            <HoldingCockpit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holdings"
        element={
          <ProtectedRoute>
            <Holdings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holdings-intraday"
        element={
          <ProtectedRoute>
            <HoldingsIntradayReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute>
            <ImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/queue"
        element={<Navigate to="/actions" replace />}
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <AuditLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <CompanySearch />
          </ProtectedRoute>
        }
      />
      <Route path="/company-metrics" element={<Navigate to="/research" replace />} />
      <Route path="/company-metrics/*" element={<Navigate to="/research" replace />} />
      <Route path="/charts" element={<Navigate to="/research" replace />} />
      <Route path="/charts/:ticker/:chartSlug" element={<LegacyChartRedirect />} />
      <Route path="/charts/:ticker" element={<LegacyChartTickerRedirect />} />
      <Route
        path="/research/workspace"
        element={
          <ProtectedRoute>
            <ResearchWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research/metrics/:metricId"
        element={
          <ProtectedRoute>
            <ResearchMetricGuide />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research/:ticker/metrics/:metricId"
        element={
          <ProtectedRoute>
            <ResearchMetricView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research/:ticker"
        element={
          <ProtectedRoute>
            <ResearchBusinessProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research"
        element={
          <ProtectedRoute>
            <ResearchDesk />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research-hub"
        element={
          <ProtectedRoute>
            <ResearchHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manual-entry"
        element={
          <ProtectedRoute>
            <ManualEntry />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cap-analysis"
        element={
          <ProtectedRoute>
            <CapAnalysis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/queue-allocator"
        element={
          <ProtectedRoute>
            <QueueAllocator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ticker-evaluation"
        element={
          <ProtectedRoute>
            <TickerEvaluation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={<Navigate to="/actions" replace />}
      />
      <Route
        path="/dividends"
        element={
          <ProtectedRoute>
            <DividendTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tax-loss"
        element={
          <ProtectedRoute>
            <TaxLossHarvesting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rebalance"
        element={
          <ProtectedRoute>
            <RebalancingCalculator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance"
        element={
          <ProtectedRoute>
            <PerformancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio-quality"
        element={
          <ProtectedRoute>
            <PortfolioQualityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sleeves"
        element={
          <ProtectedRoute>
            <SleeveOS />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review-cadence"
        element={
          <ProtectedRoute>
            <ReviewCadencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/investor-journey"
        element={
          <ProtectedRoute>
            <InvestorJourneyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/investor-score"
        element={
          <ProtectedRoute>
            <InvestorScorePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/controller"
        element={
          <ProtectedRoute>
            <ControllerSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/council-brief"
        element={
          <FeatureRoute feature="councilOrchestrator" featureName="AI Council">
            <AICouncilBrief />
          </FeatureRoute>
        }
      />
      <Route
        path="/relationships"
        element={
          <ProtectedRoute>
            <RelationshipGraph />
          </ProtectedRoute>
        }
      />
      <Route
        path="/correlation"
        element={
          <ProtectedRoute>
            <CorrelationMatrix />
          </ProtectedRoute>
        }
      />
      <Route
        path="/benchmark"
        element={
          <ProtectedRoute>
            <BenchmarkComparison />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monte-carlo"
        element={
          <ProtectedRoute>
            <MonteCarloSimulation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-reports"
        element={
          <ProtectedRoute>
            <EmailReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/batch-operations"
        element={
          <ProtectedRoute>
            <BatchOperations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/watchlist"
        element={
          <ProtectedRoute>
            <Watchlist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dca-scheduler"
        element={
          <ProtectedRoute>
            <DCAScheduler />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            <Journal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={
          <ProtectedRoute>
            <Compare />
          </ProtectedRoute>
        }
      />
      <Route
        path="/screener"
        element={
          <ProtectedRoute>
            <Screener />
          </ProtectedRoute>
        }
      />
      <Route
        path="/news"
        element={
          <ProtectedRoute>
            <NewsFeed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/xray"
        element={<Navigate to="/risk" replace />}
      />
      <Route
        path="/earnings"
        element={
          <ProtectedRoute>
            <EarningsCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/position-sizer"
        element={
          <ProtectedRoute>
            <PositionSizer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dividend-projector"
        element={
          <ProtectedRoute>
            <DividendProjector />
          </ProtectedRoute>
        }
      />
      <Route
        path="/what-if"
        element={
          <ProtectedRoute>
            <WhatIf />
          </ProtectedRoute>
        }
      />
      <Route
        path="/backtest"
        element={
          <ProtectedRoute>
            <Backtest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fx-exposure"
        element={
          <ProtectedRoute>
            <FXExposure />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treemap"
        element={
          <ProtectedRoute>
            <PortfolioTreemap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holdings-compare"
        element={
          <ProtectedRoute>
            <HoldingsComparison />
          </ProtectedRoute>
        }
      />
      <Route
        path="/share"
        element={
          <ProtectedRoute>
            <ShareCards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/snapshot-compare"
        element={
          <ProtectedRoute>
            <SnapshotComparison />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/:ticker"
        element={
          <ProtectedRoute>
            <HoldingDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/thesis"
        element={
          <ProtectedRoute>
            <ThesisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/networth"
        element={
          <ProtectedRoute>
            <NetWorth />
          </ProtectedRoute>
        }
      />
      <Route
        path="/income"
        element={
          <ProtectedRoute>
            <IncomeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-center"
        element={<Navigate to="/actions" replace />}
      />
      <Route
        path="/mutual-funds"
        element={
          <ProtectedRoute>
            <MutualFunds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sector-heatmap"
        element={
          <ProtectedRoute>
            <SectorHeatmap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/peer-comparison"
        element={
          <ProtectedRoute>
            <PeerComparison />
          </ProtectedRoute>
        }
      />
      <Route
        path="/market-overview"
        element={
          <ProtectedRoute>
            <MarketOverview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/macro"
        element={
          <ProtectedRoute>
            <MacroEnvironment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research-copilot"
        element={
          <FeatureRoute feature="researchCopilot" featureName="Research Copilot">
            <ResearchCopilot />
          </FeatureRoute>
        }
      />
      <Route
        path="/portfolio-doctor"
        element={
          <FeatureRoute feature="portfolioDoctor" featureName="Portfolio Doctor">
            <PortfolioDoctor />
          </FeatureRoute>
        }
      />
      <Route
        path="/strategy-labs"
        element={
          <FeatureRoute feature="strategyLabs" featureName="Strategy Labs">
            <StrategyLabs />
          </FeatureRoute>
        }
      />
      <Route
        path="/beam"
        element={
          <ProtectedRoute>
            <BDDOverview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulate"
        element={
          <ProtectedRoute>
            <InvestmentSimulator />
          </ProtectedRoute>
        }
      />
      <Route path="/bdd" element={<Navigate to="/beam" replace />} />
      <Route
        path="/system-logic"
        element={
          <ProtectedRoute>
            <SystemLogicDictionary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dictionary"
        element={
          <ProtectedRoute>
            <InvestorDictionary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn/today"
        element={
          <ProtectedRoute>
            <LearnToday />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dictionary-review"
        element={
          <ProtectedRoute>
            <DictionaryReviewQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/principles"
        element={
          <ProtectedRoute>
            <PrinciplesLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quiz"
        element={
          <ProtectedRoute>
            <QuizCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn/curriculum"
        element={
          <ProtectedRoute>
            <BeamAcademy />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wealth-studio"
        element={
          <ProtectedRoute>
            <WealthStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brain"
        element={
          <ProtectedRoute>
            <BrainVisualizationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intelligence"
        element={
          <ProtectedRoute>
            <IntelligenceHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/explorer"
        element={
          <ProtectedRoute>
            <PortfolioExplorer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/health"
        element={
          <ProtectedRoute>
            <HealthDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financial-wellness"
        element={
          <ProtectedRoute>
            <FinancialWellnessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/risk"
        element={
          <ProtectedRoute>
            <RiskPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review"
        element={
          <ProtectedRoute>
            <ReviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/actions"
        element={
          <ProtectedRoute>
            <ActionCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app-versions"
        element={
          <ProtectedRoute>
            <AppVersions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/discover"
        element={
          <ProtectedRoute>
            <DiscoverPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/moonshot-os/:ticker"
        element={
          <ProtectedRoute>
            <MoonshotDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lens-diagnostics"
        element={
          <ProtectedRoute>
            <LensDistinctiveness />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <CandidatePipeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lens-alerts"
        element={
          <ProtectedRoute>
            <LensAlerts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/allocation"
        element={
          <ProtectedRoute>
            <AllocationController />
          </ProtectedRoute>
        }
      />
      <Route
        path="/valuation"
        element={
          <ProtectedRoute>
            <ValuationDrilldown />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger"
        element={
          <ProtectedRoute>
            <TransactionLedger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pl-analytics"
        element={
          <ProtectedRoute>
            <PLAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-engine"
        element={
          <FeatureRoute feature="aiEngine" featureName="AI Engine">
            <AIEngineDashboard />
          </FeatureRoute>
        }
      />
      <Route
        path="/ai-clone"
        element={
          <FeatureRoute feature="aiStrictClone" featureName="AI Clone Portfolio">
            <AIClonePortfolio />
          </FeatureRoute>
        }
      />
      <Route
        path="/ai-compare"
        element={
          <FeatureRoute feature="aiStrictClone" featureName="Clone Comparison">
            <AICompare />
          </FeatureRoute>
        }
      />
      <Route
        path="/ai-approval"
        element={
          <FeatureRoute feature="aiStrictClone" featureName="AI Approval Queue">
            <AIApproval />
          </FeatureRoute>
        }
      />
      <Route
        path="/ai-what-changed"
        element={
          <FeatureRoute feature="aiStrictClone" featureName="AI What Changed">
            <AIWhatChanged />
          </FeatureRoute>
        }
      />
      <Route
        path="/ai-history"
        element={
          <FeatureRoute feature="aiStrictClone" featureName="AI Run History">
            <AIHistory />
          </FeatureRoute>
        }
      />
      <Route
        path="/account-activity"
        element={
          <ProtectedRoute>
            <AccountActivity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/access-control"
        element={
          <ProtectedRoute>
            <AccessControl />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fund-settings"
        element={
          <ProtectedRoute>
            <FundSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/decision-ledger"
        element={
          <ProtectedRoute>
            <DecisionLedger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/neo-intelligence"
        element={
          <PrivateOwnerRoute loadPrivate={() => import("@/pages/NEOIntelligence")} />
        }
      />
      <Route
        path="/fund-os"
        element={
          <PrivateOwnerRoute loadPrivate={() => import("@/pages/FundOS")} />
        }
      />
      <Route path="/" element={<LoginPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}

function App() {
  return (
    <SWRConfig value={{
      fetcher: swrFetcher,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000,
    }}>
      <div className="App">
        <BrowserRouter>
          <SessionProvider>
            <CurrencyProvider>
              <LanguageProvider>
                <WorkspaceProvider>
                  <EntitlementsProvider>
                    <AppErrorBoundary>
                      <AppRouter />
                    </AppErrorBoundary>
                    <InstallPrompt />
                    <UpdateToast />
                    <Toaster />
                  </EntitlementsProvider>
                </WorkspaceProvider>
              </LanguageProvider>
            </CurrencyProvider>
          </SessionProvider>
        </BrowserRouter>
      </div>
    </SWRConfig>
  );
}

function UpdateToast() {
  useEffect(() => {
    let toastShown = false;
    const handler = (e) => {
      if (toastShown) return;
      toastShown = true;
      const reg = e.detail?.registration;
      toast("Update Available", {
        description: "A new version of Portfolio OS is ready. Refresh when you are in a safe stopping point.",
        duration: Infinity,
        action: {
          label: "Refresh",
          onClick: () => {
            reg?.waiting?.postMessage({ type: "SKIP_WAITING" });
            window.location.reload();
          },
        },
        cancel: {
          label: "Later",
          onClick: () => {
            toastShown = false;
          },
        },
      });
    };
    window.addEventListener("sw-update-available", handler);
    return () => window.removeEventListener("sw-update-available", handler);
  }, []);
  return null;
}

export default App;
